import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import useQueryParams from '../../hooks/useQueryParams';
import useWhiteLabelComponent from '../../hooks/useWhiteLabelComponent';

import { Container, Tab } from './Tabs.styles';
import { isCCTVConnect } from '../../utils/getHost';
import Span from '../span';

import translationKeys from '../../translations/keys';
import { Trans, useTranslation } from 'react-i18next';
import { isReadableContrast } from '../../utils/colours';
import P from '../p';
import usePopup from '../../hooks/usePopup';
import Popup from '../popup';
import Button from '../button';

/** Renders a single tab item */
const TabItem = ({ 
    link, 
    location, 
    params, 
    isTabActive, 
    accentColour, 
    accentColour2, 
    useAccentColour2, 
    isVideoloft, 
    large 
}) => {
    const { t } = useTranslation();
    const { text, path, beta, badgeText, name, description, trial } = link;

    const [popupProps, triggerProps] = usePopup({
        placement: 'top'
    });

    return (
        <Tab
            key={path ?? name}
            to={
                // Use path if provided
                // Otherwise using query string, so link is current path with new tab in query string
                path ??
                `${location.pathname}?${qs.stringify({
                    ...params,
                    tab: name,
                })}`
            }
            // Need to use isActive prop if managing tab through query string
            isActive={
                path
                    ? undefined
                    : () => isTabActive(name)
            }
            $accentColour={useAccentColour2 ? accentColour2 : accentColour}
            $useDefault={isCCTVConnect}
            activeClassName="selected"
            $large={large}
            {...triggerProps}
        >
            {text}
            {
                (beta || trial || badgeText) && <Span
                    style={{
                        fontSize: '80%',
                        padding: '2px 8px',
                        background: accentColour,
                        borderRadius: 50,
                        fontWeight: 100,
                        color: isReadableContrast(accentColour, '#000') ? '#000' : '#fff',
                        marginLeft: 5,
                        marginRight: 5
                    }}
                >
                    {badgeText ?? (trial ? t(translationKeys.common.FREE_TRIAL) : t(translationKeys.technical.BETA))}
                </Span>
            }
            {
                description && <Popup {...popupProps}>
                    {
                        description.tooltipType === 'smart-motion-trial-ended' ? (
                            <>
                                <P style={{ fontWeight: 700 }}>
                                    {t('analytics.SMART_MOTION_TRIAL_ENDED_TOOLTIP_TITLE')}
                                </P>
                                <P style={{ fontSize: '80%' }}>
                                    {
                                        description.trialEndedRoute === 'self-reg' ? (
                                            <Trans
                                                i18nKey='analytics.SMART_MOTION_TRIAL_ENDED_TOOLTIP_SELF_REG_TEXT'
                                                components={{ bold: <strong /> }}
                                            />
                                        ) : (
                                            t('analytics.SMART_MOTION_TRIAL_ENDED_TOOLTIP_INSTALLER_TEXT')
                                        )
                                    }
                                </P>
                                {description.trialEndedRoute === 'self-reg' && (
                                    <Button onClick={() => window.location.assign('/manage-plan/plan')}>
                                        {t(translationKeys.navigation.MANAGE)}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <P>
                                    {trial ? t(description.heading, { date: trial ? trial.toFormat('d MMMM, yyyy') : undefined }) : text}
                                </P>
                                <P style={{ fontSize: '80%' }}>
                                    {t(description.text)}
                                </P>
                                {description.link && (
                                    <Button onClick={() => window.open(description.link)}>
                                        {t(translationKeys.common.LEARN_MORE)}
                                    </Button>
                                )}
                            </>
                        )
                    }
                </Popup>
                //     heading={t(description.heading, { date: trial ? ` ${new Date(trial * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}` : undefined })}
                //     content={<><P>{t(description.text)}</P>{isVideoloft && description.link && <a href={description.link} target="_blank">{t(translationKeys.common.LEARN_MORE)}</a>}</>}
                //     iconColour={isTabActive(name) ? useAccentColour2 ? accentColour2 : accentColour : undefined}
                // />
            }
        </Tab>
    );
};

TabItem.propTypes = {
    link: PropTypes.shape({
        text: PropTypes.string.isRequired,
        path: PropTypes.string,
        name: PropTypes.string,
        beta: PropTypes.bool,
        badgeText: PropTypes.string,
        trial: PropTypes.number,
        description: PropTypes.shape({
            heading: PropTypes.string,
            text: PropTypes.string,
            link: PropTypes.string,
            tooltipType: PropTypes.string,
            trialEndedRoute: PropTypes.string,
        }),
    }).isRequired,
    location: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
    isTabActive: PropTypes.func.isRequired,
    accentColour: PropTypes.string,
    accentColour2: PropTypes.string,
    useAccentColour2: PropTypes.bool,
    isVideoloft: PropTypes.bool,
    large: PropTypes.bool,
};

/** Renders row of tabs for switching between page content. */
const Tabs = ({ links, large = false, defaultTabName,...props }) => {
    const location = useLocation();
    const { tab, ...params } = useQueryParams();
    const { accentColour, accentColour2, useAccentColour2, isVideoloft } = useWhiteLabelComponent();

    const isTabActive = (name) => {
        if (tab) {
            return tab === name;
        } else {
            return defaultTabName === name;
        }
    };

    return (
        <Container {...props}>
            {links.map((link) => (
                <TabItem
                    key={link.path ?? link.name}
                    link={link}
                    location={location}
                    params={params}
                    isTabActive={isTabActive}
                    accentColour={accentColour}
                    accentColour2={accentColour2}
                    useAccentColour2={useAccentColour2}
                    isVideoloft={isVideoloft}
                    large={large}
                />
            ))}
        </Container>
    );
};


Tabs.propTypes = {
    /** Links to go in tabs. Tabs will be rendered in the order specified by this array. */
    links: PropTypes.arrayOf(
        PropTypes.shape({
            /** Text for nav link. */
            text: PropTypes.string.isRequired,
            /** Link destination. Required when controlling tabs through pathname rather than query string. */
            path: PropTypes.string,
            /** Name to use in `tab` query param. Must be unique. Required when controlling tabs through query string rather than pathname. */
            name: PropTypes.string,
            /** Optional custom badge text shown in tab capsule. */
            badgeText: PropTypes.string,
            /** Only for when controlling tabs in query string. When no `tab` param is present, this boolean dictates the default tab. Only one link can have `default: true`. */
            defaultTab: PropTypes.bool,
        })
    ).isRequired,
    /** Component has two possible sizes: normal or large. */
    large: PropTypes.bool,
};

export default Tabs;
